import React from 'react';
import { bool, object, shape, string, number } from 'prop-types';
import useFormatPrice from '../../utils/hooks/formatPriceHook';
import useVisualSearch from '../../utils/hooks/visualSearchHook';
import { StyledList, StyledProductCard } from './styles';
import { BODEGA_DEFAULTS } from '../../constants';
import { getProductImg } from '../../utils/url';
import templateString from '../../utils/templateString';
import useBodega from '../../utils/hooks/bodegaHook';
import Loading from '../Loading';
import { AnalyticsContext } from '../../context/analyticsContext';

/**
 * A styled product card complete with a product image, price and information surrounding the
 * product and a reference, which is to be used to focus on direct for accessibility purposes.
 * @param {object} props
 * @param {object} props.product contains details surrounding this product for the consumer to see.
 * @param {string} props.searchTerm search term used to derive this product card as a result.
 * @param {object} props.searchResultsRef reference to be used for accessibility purposes.
 * @param {string} props.resultsLength length of collection results.
 * @param {object} props.index index within the search collection.
 * @returns
 */
function VisualSearchProductCard({
  product,
  searchTerm,
  searchResultsRef,
  resultsLength,
  index,
  useSquarishOnly,
}) {
  const {
    copy,
    prices,
    pdpUrl,
    globalProductId: productId,
    featuredAttributes,
    colorwayImages,
  } = product;
  const { title, subTitle: category } = copy;
  const { url: href } = pdpUrl;
  const nikeId = featuredAttributes?.includes('CUSTOMIZED');

  const { price, discountPrice } = useFormatPrice(prices);
  const { analyticsClient } = React.useContext(AnalyticsContext);

  const handleClick = () => {
    analyticsClient?.visualSearchItemClick({
      searchTerm: searchTerm,
      itemSelected: productId,
    });
    analyticsClient?.searchCollectionItemClicked({
      searchText: searchTerm,
      product: product,
      length: resultsLength,
      index: index,
    });
  };

  // Use squarishURL for all images if useSquarishOnly is true
  const imageSrc = useSquarishOnly ? colorwayImages?.squarishURL : getProductImg(product);

  return (
    <li>
      <StyledProductCard
        imageAspectRatio={nikeId || useSquarishOnly ? '1/1' : '4/5'}
        onClick={handleClick}
        products={[
          {
            hero: {
              image: {
                src: imageSrc,
                alt: title,
              },
            },
            details: {
              title,
              category,
              price,
              discountPrice,
            },
            link: {
              href,
              title,
            },
          },
        ]}
        ref={searchResultsRef}
      />
    </li>
  );
}

/**
 * List of product cards which are displayed to the consumer that match the selected search term
 * and when clicked, will relocate the consumer to the appropriate product wall page. Each product
 * card will contain information about the product for the consumer to view.
 * @param {object} props
 * @param {object} props.selectedSuggestion the selected suggetion which determines the products
 * shown here.
 * @param {object} props.searchResultsRef reference to the first search result product card.
 * @returns
 */
export default function VisualSearchProducts({ selectedSuggestion, searchResultsRef }) {
  const { analyticsClient } = React.useContext(AnalyticsContext);
  const { visualSearchResults, loading, useSquarishOnly } = useVisualSearch(selectedSuggestion);
  const productCountLabel = templateString(
    useBodega('nav_header_label_search_product_count') ||
      BODEGA_DEFAULTS.SEARCH_PRODUCT_COUNT_RESULTS,
    {
      filteredProductCount: visualSearchResults.length.toString(),
    },
  );

  if (loading) {
    return <Loading />;
  }

  if (!visualSearchResults.length) {
    return null;
  }

  visualSearchResults.map((product, index) =>
    analyticsClient?.searchCollectionItemViewed({
      index: index,
      product: product,
      visualSearchResults: visualSearchResults,
      searchText: selectedSuggestion?.searchText || ' ',
    }),
  );

  return (
    <StyledList
      aria-label={productCountLabel}
      aria-live="assertive"
      data-testid="visual-search-products"
    >
      {visualSearchResults.map((product, index) => (
        <VisualSearchProductCard
          index={index}
          key={index}
          product={product}
          resultsLength={visualSearchResults.length}
          searchResultsRef={index === 0 ? searchResultsRef : null}
          searchTerm={selectedSuggestion?.searchText || ''}
          useSquarishOnly={useSquarishOnly}
        />
      ))}
    </StyledList>
  );
}

VisualSearchProducts.propTypes = {
  selectedSuggestion: object,
  searchResultsRef: object,
};

VisualSearchProductCard.propTypes = {
  product: shape({
    styleName: string,
    productLine2: string,
    prices: shape({
      currency: string,
      finalPrice: string,
      listPrice: string,
      onSale: bool,
    }),
    styleNumber: string,
    colorCode: string,
    imageUrls: shape({
      squarishUrl: string,
      portraitUrl: string,
    }),
    nikeId: bool,
    url: string,
  }).isRequired,
  searchTerm: string,
  searchResultsRef: object,
  resultsLength: number,
  index: number,
  useSquarishOnly: bool,
};
